import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowLeft, FaArrowRight, FaFileExcel, FaFilePdf, FaPrint, FaSearch, FaSort, FaSortUp, FaSortDown, FaChevronDown, FaEyeSlash, FaColumns, FaGripVertical, FaUndo } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import TransactionDocumentModal from './TransactionDocumentModal';

const INITIAL_COLUMNS = [
    { id: 'postingDateTime', label: 'Date', visible: true, width: '120px' },
    { id: 'itemName', label: 'Item Name', visible: true, width: '180px' },
    { id: 'brand', label: 'Brand', visible: true, width: '120px' },
    { id: 'stockUom', label: 'Stock UOM', visible: true, width: '100px' },
    { id: 'qtyIn', label: 'In Qty', visible: true, width: '100px', bgColor: '#10b981', color: '#fff' },
    { id: 'qtyOut', label: 'Out Qty', visible: true, width: '100px', bgColor: '#ef4444', color: '#fff' },
    { id: 'balanceQty', label: 'Balance Qty', visible: true, width: '120px', bgColor: '#1e293b', color: '#fff' },
    { id: 'supplier', label: 'Supplier', visible: true, width: '140px' },
    { id: 'rate', label: 'Rate', visible: true, width: '120px' },
    { id: 'averageRate', label: 'Average Rate', visible: true, width: '120px' },
    { id: 'balanceRate', label: 'Balance Rate', visible: true, width: '120px' },
    { id: 'company', label: 'Company', visible: true, width: '120px' },
    { id: 'transactionType', label: 'Type', visible: true, width: '130px' },
    { id: 'referenceId', label: 'Reference', visible: true, width: '140px', bgColor: '#3b82f6', color: '#fff' },
    { id: 'transactionValue', label: 'Trans. Value', visible: true, width: '140px', bgColor: '#f8fafc', color: '#1e293b' },
    { id: 'remarks', label: 'Remarks', visible: true, width: '220px' }
];

const loadSavedColumns = () => {
    try {
        const saved = localStorage.getItem('stock_ledger_column_order');
        if (saved) {
            const parsed = JSON.parse(saved);
            const ordered = [];
            parsed.forEach(p => {
                const colId = typeof p === 'string' ? p : p.id;
                const found = INITIAL_COLUMNS.find(c => c.id === colId);
                if (found) {
                    ordered.push({ ...found, visible: typeof p.visible === 'boolean' ? p.visible : found.visible });
                }
            });
            // Append any columns from INITIAL_COLUMNS that are not yet in saved order
            INITIAL_COLUMNS.forEach(c => {
                if (!ordered.some(o => o.id === c.id)) {
                    ordered.push(c);
                }
            });
            return ordered;
        }
    } catch (e) {
        console.error("Failed to load saved column order", e);
    }
    return INITIAL_COLUMNS;
};

const StockLedgerPage = () => {
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtering & Sorting States
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [filterCompany, setFilterCompany] = useState('');
    const [filterGroup, setFilterGroup] = useState('');
    const [filterItem, setFilterItem] = useState('');
    const [filterUom, setFilterUom] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'postingDateTime', direction: 'descending' });

    // Column Management & Reorder States
    const [columns, setColumns] = useState(loadSavedColumns);
    const [draggedColId, setDraggedColId] = useState(null);
    const [dragOverColId, setDragOverColId] = useState(null);
    const [activeColumnMenu, setActiveColumnMenu] = useState(null);
    const [showColumnManager, setShowColumnManager] = useState(false);

    // Modal States
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRef, setSelectedRef] = useState({ refType: '', refId: '' });

    const navigate = useNavigate();
    const menuRef = useRef(null);
    const managerRef = useRef(null);
    const draggedColRef = useRef(null);

    // Save column order & visibility to localStorage
    const saveColumns = (newCols) => {
        setColumns(newCols);
        try {
            localStorage.setItem('stock_ledger_column_order', JSON.stringify(newCols.map(c => ({ id: c.id, visible: c.visible }))));
        } catch (e) {
            console.error("Failed to save column order", e);
        }
    };

    // Reset columns to factory default
    const resetColumns = () => {
        try {
            localStorage.removeItem('stock_ledger_column_order');
        } catch (e) {}
        setColumns(INITIAL_COLUMNS);
    };

    // Move column Left (-1) or Right (+1) among visible columns
    const moveColumn = (colId, direction) => {
        const visibleCols = columns.filter(c => c.visible);
        const visibleIndex = visibleCols.findIndex(c => c.id === colId);
        if (visibleIndex === -1) return;
        const targetVisibleIndex = visibleIndex + direction;
        if (targetVisibleIndex < 0 || targetVisibleIndex >= visibleCols.length) return;

        const targetColId = visibleCols[targetVisibleIndex].id;
        const fromIdx = columns.findIndex(c => c.id === colId);
        const toIdx = columns.findIndex(c => c.id === targetColId);
        if (fromIdx === -1 || toIdx === -1) return;

        const newCols = [...columns];
        const [moved] = newCols.splice(fromIdx, 1);
        newCols.splice(toIdx, 0, moved);
        saveColumns(newCols);
    };

    // Drag-and-drop handlers
    const handleDragStart = (e, colId) => {
        draggedColRef.current = colId;
        setDraggedColId(colId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', colId);
    };

    const handleDragOver = (e, colId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedColRef.current && colId !== dragOverColId) {
            setDragOverColId(colId);
        }
    };

    const handleDrop = (e, targetColId) => {
        e.preventDefault();
        const sourceColId = draggedColRef.current || e.dataTransfer.getData('text/plain');
        if (!sourceColId || sourceColId === targetColId) {
            draggedColRef.current = null;
            setDraggedColId(null);
            setDragOverColId(null);
            return;
        }

        const fromIdx = columns.findIndex(c => c.id === sourceColId);
        const toIdx = columns.findIndex(c => c.id === targetColId);
        if (fromIdx !== -1 && toIdx !== -1) {
            const newCols = [...columns];
            const [moved] = newCols.splice(fromIdx, 1);
            newCols.splice(toIdx, 0, moved);
            saveColumns(newCols);
        }
        draggedColRef.current = null;
        setDraggedColId(null);
        setDragOverColId(null);
    };

    const handleDragEnd = () => {
        draggedColRef.current = null;
        setDraggedColId(null);
        setDragOverColId(null);
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setActiveColumnMenu(null);
            if (managerRef.current && !managerRef.current.contains(event.target)) setShowColumnManager(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getHeaders = () => {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        const token = localStorage.getItem('token');

        const headers = { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };

        const companyHeader = user?.company_name || user?.company || '';
        if (companyHeader) headers['X-Company-Name'] = companyHeader;

        const branchHeader = localStorage.getItem('active_branch') || user?.branch_name || user?.branch || '';
        if (branchHeader) headers['X-Branch-Name'] = branchHeader;

        return headers;
    };

    const fetchLedger = async () => {
        setLoading(true);
        try {
            const params = {};
            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;

            const res = await axios.get('/api/inventory/stock-ledger', { params, headers: getHeaders() });
            const validLedger = res.data.filter(entry =>
                (entry.itemName || entry.item_name) &&
                (entry.itemName !== 'Unknown Item' && entry.item_name !== 'Unknown Item')
            );
            setLedger(validLedger);
        } catch (err) {
            console.error("Error fetching stock ledger", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLedger();
    }, [fromDate, toDate]); // Fetch when dates change

    // ─── Extract dropdown options ───
    const uniqueCompanies = [...new Set(ledger.map(entry => entry.company || '').filter(Boolean))];
    const uniqueGroups = [...new Set(ledger.map(entry => entry.brand || entry.category || '').filter(Boolean))]; // Using brand as group placeholder if category isn't there
    const uniqueItems = [...new Set(ledger.map(entry => entry.itemName || entry.item_name || '').filter(Boolean))];
    const uniqueUoms = [...new Set(ledger.map(entry => entry.stockUom || entry.uom || entry.unit || '').filter(Boolean))];

    // ─── Filtering & Sorting Logic ───
    let processedLedger = ledger.filter(entry => {
        const companyMatch = filterCompany ? (entry.company || '') === filterCompany : true;
        const groupMatch = filterGroup ? (entry.brand || entry.category || '') === filterGroup : true;
        const itemMatch = filterItem ? (entry.itemName || entry.item_name || '') === filterItem : true;
        const uom = entry.stockUom || entry.uom || entry.unit || '';
        const uomMatch = filterUom ? uom === filterUom : true;
        return companyMatch && groupMatch && itemMatch && uomMatch;
    });

    processedLedger.sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = a[sortConfig.key] || 0;
        let bVal = b[sortConfig.key] || 0;

        if (sortConfig.key === 'postingDateTime' || sortConfig.key === 'transaction_date') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
        } else if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
    });

    const openDocumentModal = (refType, refId) => {
        if (!refId || refId === '-') return;
        setSelectedRef({ refType, refId });
        setModalOpen(true);
    };

    // ─── Export helpers ────────────────────────────────────────────────────────
    const exportToExcel = () => {
        const visibleCols = columns.filter(c => c.visible);
        const exportData = processedLedger.map(row => {
            const expRow = {};
            visibleCols.forEach(c => {
                if (c.id === 'postingDateTime') expRow[c.label] = new Date(row.postingDateTime || row.transaction_date).toLocaleString();
                else if (c.id === 'itemName') expRow[c.label] = row.itemName || row.item_name || '-';
                else if (['qtyIn', 'qtyOut', 'balanceQty'].includes(c.id)) expRow[c.label] = Number(row[c.id] || 0).toFixed(2);
                else if (['rate', 'averageRate', 'balanceRate'].includes(c.id)) expRow[c.label] = Number(row[c.id] || 0).toFixed(4);
                else if (c.id === 'transactionValue') expRow[c.label] = Number(row[c.id] || 0).toFixed(2);
                else if (c.id === 'referenceId') expRow[c.label] = `${row.referenceType || '-'} : ${row.referenceId || '-'}`;
                else if (c.id === 'remarks') expRow[c.label] = row[c.id] || '-';
                else expRow[c.label] = row[c.id] || '-';
            });
            return expRow;
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Ledger");
        XLSX.writeFile(workbook, "Stock_Ledger.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF('landscape');
        doc.text("Stock Ledger Report", 14, 15);
        const visibleCols = columns.filter(c => c.visible);
        const tableColumn = visibleCols.map(c => c.label);
        const tableRows = processedLedger.map(row => {
            return visibleCols.map(c => {
                if (c.id === 'postingDateTime') return new Date(row.postingDateTime || row.transaction_date).toLocaleDateString();
                if (c.id === 'itemName') return row.itemName || row.item_name || '-';
                if (['qtyIn', 'qtyOut', 'balanceQty'].includes(c.id)) return Number(row[c.id] || 0).toFixed(2);
                if (['rate', 'averageRate', 'balanceRate', 'transactionValue'].includes(c.id)) return Number(row[c.id] || 0).toFixed(2);
                if (c.id === 'referenceId') return row.referenceId || '-';
                if (c.id === 'remarks') return row.remarks || '-';
                return row[c.id] || '-';
            });
        });
        doc.autoTable({ 
            head: [tableColumn], 
            body: tableRows, 
            startY: 20, 
            theme: 'grid', 
            headStyles: { fillColor: [43, 45, 66], fontSize: 6.5 }, 
            styles: { fontSize: 6, cellPadding: 1.5, overflow: 'linebreak' } 
        });
        doc.save("Stock_Ledger.pdf");
    };

    // ─── Table Cell Renderer ───
    const renderCell = (row, colId) => {
        switch (colId) {
            case 'postingDateTime': return <div style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>{new Date(row.postingDateTime || row.transaction_date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>;
            case 'itemName': return <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>{row.itemName || row.item_name}</div>;
            case 'brand':
            case 'supplier':
            case 'company': return <div style={{ color: '#64748b', fontSize: '13px' }}>{row[colId] || '-'}</div>;
            case 'stockUom': return <div style={{ color: '#64748b', fontSize: '13px', textTransform: 'uppercase' }}>{row.stockUom || '-'}</div>;
            case 'qtyIn': return <div style={{ fontWeight: '700', color: '#10b981' }}>{Number(row.qtyIn || 0) > 0 ? `+${Number(row.qtyIn).toFixed(2)}` : '-'}</div>;
            case 'qtyOut': return <div style={{ fontWeight: '700', color: '#ef4444' }}>{Number(row.qtyOut || 0) > 0 ? `-${Number(row.qtyOut).toFixed(2)}` : '-'}</div>;
            case 'balanceQty': return <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{Number(row.balanceQty || 0).toFixed(2)}</div>;
            case 'rate':
            case 'averageRate': return <div style={{ fontWeight: '600', color: '#64748b' }}>₹ {Number(row[colId] || 0).toFixed(4)}</div>;
            case 'balanceRate': return <div style={{ fontWeight: '700', color: '#1e293b' }}>₹ {Number(row.balanceRate || 0).toFixed(4)}</div>;
            case 'transactionType':
                const tType = row.transactionType || row.transaction_type;
                const isRed = ['SALE', 'SUPPLIER_RETURN', 'TRANSFER_OUT', 'WASTE', 'ADJUSTMENT_OUT'].includes(tType);
                return <span style={{ background: isRed ? '#fef2f2' : '#ecfdf5', color: isRed ? '#ef4444' : '#10b981', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>{tType || '-'}</span>;
            case 'referenceId':
                return <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>{row.referenceType || row.reference_type || '-'}</span>
                    <span onClick={() => openDocumentModal(row.referenceType || row.reference_type, row.referenceId || row.reference_id)} style={{ color: '#3b82f6', fontWeight: '600', fontSize: '12px', cursor: (row.referenceId || row.reference_id) && (row.referenceId || row.reference_id) !== '-' ? 'pointer' : 'default', textDecoration: (row.referenceId || row.reference_id) && (row.referenceId || row.reference_id) !== '-' ? 'underline' : 'none' }}>
                        {row.referenceId || row.reference_id || '-'}
                    </span>
                </div>;
            case 'transactionValue': return <div style={{ fontWeight: '700', color: '#0f172a' }}>₹ {Number(row.transactionValue || 0).toFixed(2)}</div>;
            case 'remarks': return <div className="remarks-cell" style={{ color: '#475569', fontSize: '12px', fontStyle: 'italic', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{row.remarks || '-'}</div>;
            default: return null;
        }
    };

    return (
        <div style={{ backgroundColor: "#F4F7FC", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: "'Outfit', sans-serif", color: "#2B2D42" }}>

            {/* Header Area */}
            <div className="no-print" style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderBottom: '1px solid #E2E8F0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ background: '#f8fafc', border: '1px solid #E2E8F0', padding: '10px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', color: '#475569', transition: '0.2s' }}
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <div>
                        <h2 style={{ margin: '0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>Stock Ledger</h2>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>Immutable historical transaction source of truth</p>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                    {/* Add Columns Button */}
                    <div ref={managerRef} style={{ position: 'relative' }}>
                        <button onClick={() => setShowColumnManager(!showColumnManager)} className="export-btn" style={{ background: '#f8fafc' }}>
                            <FaColumns style={{ color: '#3b82f6' }} /> Columns
                        </button>
                        {showColumnManager && (
                            <div className="column-manager-dropdown" style={{ width: '270px' }}>
                                <div style={{ padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>Manage & Reorder</span>
                                    <button onClick={resetColumns} title="Reset to default column order" style={{ background: 'transparent', border: 'none', color: '#3b82f6', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                                        <FaUndo style={{ fontSize: '10px' }} /> Reset
                                    </button>
                                </div>
                                <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '6px 0' }}>
                                    {columns.map((col, cIdx) => (
                                        <div key={col.id} className="column-manager-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px' }}>
                                            <div 
                                                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1, minWidth: 0 }}
                                                onClick={() => {
                                                    const newCols = columns.map(c => c.id === col.id ? { ...c, visible: !c.visible } : c);
                                                    saveColumns(newCols);
                                                }}
                                            >
                                                <input type="checkbox" checked={col.visible} readOnly style={{ cursor: 'pointer' }} />
                                                <span style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.label}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                                                <button 
                                                    disabled={cIdx === 0} 
                                                    onClick={(e) => { e.stopPropagation(); moveColumn(col.id, -1); }}
                                                    style={{ border: '1px solid #e2e8f0', background: cIdx === 0 ? '#f8fafc' : '#ffffff', borderRadius: '4px', padding: '2px 6px', cursor: cIdx === 0 ? 'not-allowed' : 'pointer', opacity: cIdx === 0 ? 0.35 : 1, fontSize: '11px', fontWeight: 'bold' }}
                                                    title="Move Column Left"
                                                >
                                                    ←
                                                </button>
                                                <button 
                                                    disabled={cIdx === columns.length - 1} 
                                                    onClick={(e) => { e.stopPropagation(); moveColumn(col.id, 1); }}
                                                    style={{ border: '1px solid #e2e8f0', background: cIdx === columns.length - 1 ? '#f8fafc' : '#ffffff', borderRadius: '4px', padding: '2px 6px', cursor: cIdx === columns.length - 1 ? 'not-allowed' : 'pointer', opacity: cIdx === columns.length - 1 ? 0.35 : 1, fontSize: '11px', fontWeight: 'bold' }}
                                                    title="Move Column Right"
                                                >
                                                    →
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <button onClick={exportToExcel} className="export-btn"><FaFileExcel style={{ color: '#107c41', fontSize: '16px' }} /> Excel</button>
                    <button onClick={exportToPDF} className="export-btn"><FaFilePdf style={{ color: '#d32f2f', fontSize: '16px' }} /> PDF</button>
                    <button onClick={() => window.print()} className="export-btn"><FaPrint style={{ color: '#334155', fontSize: '16px' }} /> Print</button>
                </div>
            </div>

            {/* Premium Filter Bar exactly matching the user screenshot */}
            <div className="no-print" style={{ background: '#ffffff', padding: '20px 32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company Name</label>
                    <select className="premium-filter-input" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
                        <option value="">Filter Company Name...</option>
                        {uniqueCompanies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From Date</label>
                    <input type="date" className="premium-filter-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From Date" />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To Date</label>
                    <input type="date" className="premium-filter-input" value={toDate} onChange={(e) => setToDate(e.target.value)} title="To Date" />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Item Group</label>
                    <select className="premium-filter-input" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                        <option value="">Filter Item Group...</option>
                        {uniqueGroups.map((g, i) => <option key={i} value={g}>{g}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items</label>
                    <select className="premium-filter-input" value={filterItem} onChange={(e) => setFilterItem(e.target.value)}>
                        <option value="">Filter Items...</option>
                        {uniqueItems.map((item, i) => <option key={i} value={item}>{item}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Stock UOM</label>
                    <select className="premium-filter-input" value={filterUom} onChange={(e) => setFilterUom(e.target.value)}>
                        <option value="">Filter Stock UOM...</option>
                        {uniqueUoms.map((uom, i) => <option key={i} value={uom}>{uom}</option>)}
                    </select>
                </div>
            </div>

            {/* Table Area */}
            <div style={{ padding: '24px 32px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#ffffff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', flex: 1, overflow: 'auto', position: 'relative' }}>

                    {/* Print Only Header */}
                    <div className="print-only" style={{ textAlign: 'center', marginBottom: '16px', marginTop: '16px' }}>
                        <h2 style={{ fontSize: '16pt', margin: 0, padding: 0, color: '#000', fontWeight: 'bold' }}>Stock Ledger Report</h2>
                        <p style={{ margin: '4px 0 0 0', padding: 0, fontSize: '10pt', color: '#000' }}>Date: {new Date().toLocaleDateString()}</p>
                    </div>

                    {loading ? (
                        <div style={{ padding: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <div className="loader"></div>
                            <p style={{ marginTop: '16px', color: '#64748b' }}>Loading ledger data...</p>
                        </div>
                    ) : processedLedger.length === 0 ? (
                        <div style={{ padding: '64px', textAlign: 'center', color: '#94a3b8', fontSize: '16px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No transactions found for the current filters.</p>
                        </div>
                    ) : (
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    {columns.filter(c => c.visible).map((col, idx, arr) => (
                                        <th 
                                            key={col.id} 
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, col.id)}
                                            onDragOver={(e) => handleDragOver(e, col.id)}
                                            onDrop={(e) => handleDrop(e, col.id)}
                                            onDragEnd={handleDragEnd}
                                            className={`col-draggable-th col-${col.id} ${draggedColId === col.id ? 'col-is-dragging' : ''} ${dragOverColId === col.id ? 'col-drag-over' : ''}`}
                                            style={{ background: col.bgColor || '#ffffff', color: col.color || '#475569', minWidth: col.width }}
                                            title="Drag to reorder column left or right"
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                                <span className="drag-handle no-print" title="Drag column left/right">
                                                    <FaGripVertical />
                                                </span>

                                                <div
                                                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}
                                                    onClick={() => setSortConfig(s => ({ key: col.id, direction: s.key === col.id && s.direction === 'ascending' ? 'descending' : 'ascending' }))}
                                                >
                                                    {col.label}
                                                </div>

                                                {/* Dropdown Menu Trigger */}
                                                <div className="col-menu-trigger" onClick={(e) => { e.stopPropagation(); setActiveColumnMenu(activeColumnMenu === col.id ? null : col.id); }}>
                                                    <FaChevronDown />
                                                </div>

                                                {/* Dropdown Menu */}
                                                {activeColumnMenu === col.id && (
                                                    <div className="col-menu-dropdown" ref={menuRef} style={{ left: col.align === 'right' ? 'auto' : '0', right: col.align === 'right' ? '0' : 'auto' }} onClick={(e) => e.stopPropagation()}>
                                                        {/* Move Left / Right Options */}
                                                        {idx > 0 && (
                                                            <div className="col-menu-item" onClick={() => { moveColumn(col.id, -1); setActiveColumnMenu(null); }}>
                                                                <FaArrowLeft style={{ color: '#3b82f6' }} /> Move Left
                                                            </div>
                                                        )}
                                                        {idx < arr.length - 1 && (
                                                            <div className="col-menu-item" onClick={() => { moveColumn(col.id, 1); setActiveColumnMenu(null); }}>
                                                                <FaArrowRight style={{ color: '#3b82f6' }} /> Move Right
                                                            </div>
                                                        )}
                                                        {(idx > 0 || idx < arr.length - 1) && <div className="col-menu-divider"></div>}

                                                        <div className="col-menu-item" onClick={() => { setSortConfig({ key: col.id, direction: 'ascending' }); setActiveColumnMenu(null); }}>
                                                            <FaSortUp /> Sort Ascending
                                                        </div>
                                                        <div className="col-menu-item" onClick={() => { setSortConfig({ key: col.id, direction: 'descending' }); setActiveColumnMenu(null); }}>
                                                            <FaSortDown /> Sort Descending
                                                        </div>
                                                        <div className="col-menu-item" onClick={() => { if (sortConfig.key === col.id) setSortConfig({ key: null, direction: null }); setActiveColumnMenu(null); }}>
                                                            <FaSort style={{ opacity: 0.5 }} /> Reset sorting
                                                        </div>
                                                        <div className="col-menu-divider"></div>
                                                        <div className="col-menu-item text-red" onClick={() => {
                                                            const newCols = columns.map(c => c.id === col.id ? { ...c, visible: false } : c);
                                                            saveColumns(newCols);
                                                            setActiveColumnMenu(null);
                                                        }}>
                                                            <FaEyeSlash /> Remove column
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {processedLedger.map((row, i) => (
                                    <tr key={i}>
                                        {columns.filter(c => c.visible).map(col => (
                                            <td key={col.id} className={`col-${col.id}`} style={{ background: col.id === 'qtyIn' ? '#ecfdf5' : col.id === 'qtyOut' ? '#fef2f2' : col.id === 'balanceQty' || col.id === 'transactionValue' ? '#f8fafc' : 'inherit' }}>
                                                {renderCell(row, col.id)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Transaction Document Modal */}
            {modalOpen && (
                <TransactionDocumentModal
                    refType={selectedRef.refType}
                    refId={selectedRef.refId}
                    onClose={() => setModalOpen(false)}
                />
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
                
                .export-btn { background: #ffffff; border: 1px solid #E2E8F0; padding: 8px 16px; border-radius: 8px; display: flex; align-items: center; gap: 8px; cursor: pointer; font-weight: 600; color: #475569; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .export-btn:hover { background: #f8fafc; border-color: #cbd5e1; transform: translateY(-1px); }
                
                .premium-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .premium-table th { font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; padding: 12px 16px; text-align: left; position: sticky; top: 0; z-index: 10; border-bottom: 2px solid #e2e8f0; white-space: nowrap; user-select: none; }
                .premium-table td { padding: 12px 16px; color: #1e293b; font-size: 13px; font-weight: 500; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
                .premium-table tr:hover td { filter: brightness(0.98); }
                
                /* Column Drag and Drop Styles */
                .col-draggable-th { cursor: grab; transition: background-color 0.15s, border-color 0.15s; }
                .col-draggable-th:hover { filter: brightness(0.97); }
                .col-draggable-th:active { cursor: grabbing; }
                .col-is-dragging { opacity: 0.4; }
                .col-drag-over { border-left: 4px solid #2563eb !important; background: #e0f2fe !important; }
                .drag-handle { opacity: 0.35; cursor: grab; font-size: 11px; display: flex; align-items: center; }
                .drag-handle:hover { opacity: 1; color: #2563eb; }
                
                /* Premium Grid Filter Styles */
                .premium-filter-input {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 13px;
                    color: #64748b;
                    background: #ffffff;
                    outline: none;
                    font-family: inherit;
                    transition: all 0.2s;
                    appearance: none;
                    -webkit-appearance: none;
                }
                select.premium-filter-input {
                    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 14px;
                }
                .premium-filter-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                    color: #0f172a;
                }
                input[type="date"].premium-filter-input::-webkit-datetime-edit { color: #64748b; }
                input[type="date"].premium-filter-input:focus::-webkit-datetime-edit { color: #0f172a; }
                input[type="date"].premium-filter-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
                input[type="date"].premium-filter-input::-webkit-calendar-picker-indicator:hover { opacity: 1; }
                
                /* Column Menu Styles */
                .col-menu-trigger { padding: 4px; border-radius: 4px; color: inherit; opacity: 0.4; cursor: pointer; transition: 0.2s; display: flex; align-items: center; justify-content: center; }
                .col-menu-trigger:hover { opacity: 1; background: rgba(0,0,0,0.05); }
                
                .col-menu-dropdown { position: absolute; top: calc(100% - 8px); background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); width: 200px; z-index: 50; overflow: hidden; padding: 4px 0; animation: fadeInDown 0.2s ease; }
                .col-menu-item { padding: 10px 16px; font-size: 13px; color: #334155; display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: 500; text-transform: none; letter-spacing: 0; transition: background 0.1s; }
                .col-menu-item:hover { background: #f1f5f9; }
                .col-menu-item.text-red { color: #ef4444; }
                .col-menu-item.text-red:hover { background: #fef2f2; }
                .col-menu-divider { height: 1px; background: #e2e8f0; margin: 4px 0; }
                
                /* Column Manager Dropdown */
                .column-manager-dropdown { position: absolute; top: calc(100% + 8px); right: 0; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); width: 240px; z-index: 50; overflow: hidden; animation: fadeInDown 0.2s ease; }
                .column-manager-item { padding: 8px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: 0.1s; font-size: 13px; color: #475569; font-weight: 500; }
                .column-manager-item:hover { background: #f8fafc; color: #0f172a; }
                .column-manager-item input { accent-color: #3b82f6; width: 16px; height: 16px; }

                .loader { width: 36px; height: 36px; border: 3px solid #e2e8f0; border-top: 3px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes fadeInDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }

                @media print {
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    
                    /* Disable restrictive page height/overflow in print to allow full document flow */
                    body, html, #root { 
                        display: block !important; 
                        position: static !important; 
                        width: 100% !important; 
                        max-width: 100% !important; 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: transparent !important; 
                        overflow: visible !important; 
                        height: auto !important; 
                    }
                    
                    /* Ensure containers don't clip table */
                    div { 
                        overflow: visible !important; 
                        box-shadow: none !important; 
                    }
                    
                    /* Restore clean header spacing */
                    .print-only { margin-bottom: 16px !important; text-align: center !important; }
                    .print-only h2 { margin: 0 !important; padding: 0 !important; font-size: 15pt !important; color: #000 !important; font-weight: bold !important; }
                    .print-only p { margin: 4px 0 0 0 !important; padding: 0 !important; font-size: 9pt !important; color: #000 !important; }
                    
                    /* Allow table to distribute width cleanly across available page area */
                    .premium-table { 
                        display: table !important; 
                        width: 100% !important; 
                        max-width: 100% !important; 
                        table-layout: auto !important; 
                        margin: 0 !important; 
                        border: 1px solid #000 !important; 
                        border-collapse: collapse !important; 
                        font-size: 6.5pt !important; 
                        color: #000 !important; 
                    }
                    
                    /* position: static fixes missing borders on table headers during print */
                    .premium-table th { 
                        position: static !important; 
                        background: #f8fafc !important;
                        font-weight: 700 !important;
                    }
                    
                    /* Force crisp borders, full wrapping, and no clipping */
                    .premium-table th, .premium-table td { 
                        padding: 4px 3px !important; 
                        border: 1px solid #000 !important; 
                        color: #000 !important; 
                        white-space: normal !important; 
                        word-break: break-word !important; 
                        overflow-wrap: break-word !important; 
                        min-width: 0 !important; 
                        vertical-align: middle !important; 
                        text-align: center !important; 
                        font-size: 6.5pt !important; 
                    }
                    
                    /* Ensure all inner elements wrap and don't overflow or enforce minimum widths */
                    .premium-table th *, .premium-table td * { 
                        color: #000 !important; 
                        background: transparent !important; 
                        white-space: normal !important; 
                        word-break: break-word !important; 
                        overflow-wrap: break-word !important; 
                        font-weight: normal !important; 
                        font-size: inherit !important; 
                        letter-spacing: 0 !important; 
                        min-width: 0 !important; 
                        max-width: 100% !important; 
                        width: auto !important; 
                    }
                    
                    /* Remarks specific formatting: left aligned, wraps completely so full description is readable */
                    .premium-table th.col-remarks, .premium-table td.col-remarks { 
                        text-align: left !important; 
                        min-width: 90px !important; 
                        max-width: 180px !important; 
                    }
                    .premium-table th.col-remarks *, .premium-table td.col-remarks * { 
                        text-align: left !important; 
                        font-style: normal !important; 
                        white-space: normal !important; 
                        word-break: break-word !important; 
                        overflow-wrap: break-word !important; 
                    }

                    /* Item Name left aligned */
                    .premium-table th.col-itemName, .premium-table td.col-itemName { 
                        text-align: left !important; 
                        min-width: 75px !important; 
                    }
                    .premium-table th.col-itemName *, .premium-table td.col-itemName * { 
                        text-align: left !important; 
                    }
                    
                    .col-menu-trigger, .summary-card, .filter-dropdown, .export-btn, .drag-handle { display: none !important; }
                    
                    @page { margin: 5mm; size: landscape; }
                }
            `}</style>
        </div>
    );
};

export default StockLedgerPage;
